import { ApiError, api } from '@/lib/api';
import type { EmployeeJobDetail } from '@shared/schemas/employee-portal';

/*
 * The photo upload queue — ROADMAP.md §6.4.
 *
 * **This is the one place in the application where losing data means redoing
 * physical work.** Everywhere else a failed request is an inconvenience: retype
 * the form, reload the page. A photo of a bathroom that has since been left is
 * a photo that cannot be taken again without driving back, and without it the
 * task cannot be completed at all (business-rules.md §3.1). So an upload is not
 * a request that either works or shows an error — it is a job that is held until
 * it is done.
 *
 * The scenario it is built for is specific and real: a cleaner in a basement
 * with no signal takes ten photos over twenty minutes. Every upload fails. They
 * walk outside, signal returns, and all ten land. Nothing was lost and nobody
 * had to notice.
 *
 * ## What it survives, and what it does not
 *
 * **It survives a dropped connection, a slow one, and moving between screens** —
 * the queue is module state, outside React, so navigating from the job to the
 * dashboard and back does not touch it.
 *
 * **It does not survive closing the tab**, and that is a decision rather than an
 * omission. Persisting would mean writing photographs of the inside of
 * customers' homes into IndexedDB on a phone that may be shared, lost, or
 * borrowed — which works against every other rule about these files (private
 * bucket, signed URLs, EXIF stripped on the way in). Offline storage belongs
 * with the PWA work in 11.5, where the owner can decide it deliberately. In the
 * meantime `beforeunload` asks before the tab closes with photos still waiting,
 * which is the honest half that costs nothing.
 *
 * ## Which failures are retried
 *
 * Only the ones that might work later: a network error, a 408, a 429, or any
 * 5xx. A 400 means the file is not a photo this system accepts and a 404 means
 * the job is not theirs — retrying either forever would hide a real answer
 * behind a spinner. Those park immediately with the server's own message.
 */

export type UploadStatus = 'waiting' | 'uploading' | 'failed';

export interface QueuedUpload {
  readonly id: string;
  readonly jobId: string;
  readonly jobTaskId: string | null;
  /** For the screen — "Vacuum all carpeted areas" rather than a uuid. */
  readonly taskName: string | null;
  readonly kind: 'before' | 'after' | 'issue';
  readonly caption: string | null;
  readonly file: File;

  status: UploadStatus;
  attempts: number;
  /** The server's own words when it refused, or a network message. */
  lastError: string | null;
  /** True once retrying by hand is the only thing left. */
  exhausted: boolean;
}

/**
 * Five automatic attempts, then it waits for a person.
 *
 * Not infinite: a queue that retries forever on a phone in a dead spot is a
 * battery drain nobody asked for, and the cleaner should find out that
 * something is wrong while they are still at the house.
 */
const MAX_ATTEMPTS = 5;

/** 1s, 2s, 4s, 8s, 16s — capped, so a long outage does not turn into a long sleep. */
function backoffMs(attempts: number): number {
  return Math.min(1000 * 2 ** (attempts - 1), 16_000);
}

let queue: QueuedUpload[] = [];
let pumping = false;
let nextId = 0;

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  // A new array each time: `useSyncExternalStore` compares snapshots by
  // identity, and mutating in place would render nothing.
  queue = [...queue];
  for (const listener of listeners) listener();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSnapshot(): readonly QueuedUpload[] {
  return queue;
}

/**
 * Told when an upload lands, so the screen can refetch.
 *
 * A listener rather than the queue holding a `QueryClient`, because the queue
 * outlives every component and should not know what TanStack Query is. The
 * employee layout registers this once.
 */
type SuccessListener = (jobId: string, detail: EmployeeJobDetail) => void;
let onSuccess: SuccessListener = () => undefined;

export function setUploadSuccessListener(listener: SuccessListener): () => void {
  onSuccess = listener;
  return () => {
    onSuccess = () => undefined;
  };
}

export interface EnqueueInput {
  jobId: string;
  jobTaskId: string | null;
  taskName: string | null;
  kind: 'before' | 'after' | 'issue';
  caption: string | null;
  file: File;
}

export function enqueue(input: EnqueueInput): void {
  nextId += 1;
  queue.push({
    id: `upload-${String(nextId)}`,
    ...input,
    status: 'waiting',
    attempts: 0,
    lastError: null,
    exhausted: false,
  });
  emit();
  void pump();
}

/** Give up on one — the photo is gone, so this asks the caller to be sure. */
export function discard(id: string): void {
  queue = queue.filter((entry) => entry.id !== id);
  emit();
}

/** Try a parked upload again, now. */
export function retry(id: string): void {
  const entry = queue.find((item) => item.id === id);
  if (!entry) return;
  entry.status = 'waiting';
  entry.attempts = 0;
  entry.exhausted = false;
  entry.lastError = null;
  emit();
  void pump();
}

/** How many are still to go, for the badge and the beforeunload guard. */
export function pendingCount(): number {
  return queue.filter((entry) => entry.status !== 'failed' || !entry.exhausted).length;
}

/** Everything still queued for one job, for that job's screen. */
export function pendingForJob(jobId: string): QueuedUpload[] {
  return queue.filter((entry) => entry.jobId === jobId);
}

/**
 * Whether this failure is worth trying again.
 *
 * A 400 is "that is not a photo this system accepts" and a 404 is "that job is
 * not yours" — both are answers, and hiding an answer behind five more attempts
 * wastes the cleaner's time at the house. A 0 is our own network error from
 * `lib/api`, which is exactly the case this queue exists for.
 */
function isRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 0) return true;
  if (error.status === 408 || error.status === 429) return true;
  return error.status >= 500;
}

function formData(entry: QueuedUpload): FormData {
  const form = new FormData();
  form.set('photo', entry.file);
  if (entry.jobTaskId !== null) form.set('jobTaskId', entry.jobTaskId);
  form.set('kind', entry.kind);
  if (entry.caption !== null) form.set('caption', entry.caption);
  return form;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * One at a time, in order.
 *
 * Serial rather than parallel on purpose: ten concurrent uploads on a phone
 * with one bar are ten timeouts instead of one success, and the crew took the
 * photos in an order that is worth keeping.
 */
async function pump(): Promise<void> {
  if (pumping) return;
  pumping = true;

  try {
    for (;;) {
      const entry = queue.find((item) => item.status === 'waiting');
      if (!entry) return;

      entry.status = 'uploading';
      entry.attempts += 1;
      emit();

      try {
        const detail = await api.upload<EmployeeJobDetail>(
          `/api/employee/jobs/${entry.jobId}/photos`,
          formData(entry),
        );

        // Off the queue before the listener runs, so a refetch it triggers sees
        // the queue as it will be rather than as it was.
        queue = queue.filter((item) => item.id !== entry.id);
        emit();
        onSuccess(entry.jobId, detail);
      } catch (error) {
        entry.lastError =
          error instanceof ApiError ? error.message : 'The upload failed. It will be retried.';

        if (!isRetryable(error) || entry.attempts >= MAX_ATTEMPTS) {
          entry.status = 'failed';
          entry.exhausted = true;
          emit();
          // Keep going: one bad photo must not hold up the nine behind it.
          continue;
        }

        entry.status = 'waiting';
        emit();
        await sleep(backoffMs(entry.attempts));
      }
    }
  } finally {
    pumping = false;
  }
}

/**
 * Wire the queue to the browser. Called once, from the employee layout.
 *
 * `online` is the one that matters: walking out of a basement should flush the
 * queue immediately rather than waiting out a sixteen-second backoff, and the
 * browser tells us the moment it happens.
 */
export function installQueueListeners(): () => void {
  function onOnline(): void {
    for (const entry of queue) {
      if (entry.status === 'failed' && !entry.exhausted) entry.status = 'waiting';
    }
    emit();
    void pump();
  }

  function onBeforeUnload(event: BeforeUnloadEvent): void {
    if (pendingCount() === 0) return;
    // The queue is in memory only (see the file comment), so closing the tab
    // really does lose these. Asking is the least this can do.
    event.preventDefault();
  }

  window.addEventListener('online', onOnline);
  window.addEventListener('beforeunload', onBeforeUnload);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('beforeunload', onBeforeUnload);
  };
}

/** Testing seam — the queue is module state and a test needs a clean one. */
export function resetQueue(): void {
  queue = [];
  nextId = 0;
  emit();
}
